import { ref, computed, nextTick } from 'vue'
import { useWindowSize, useElementBounding } from '@vueuse/core'

import type { Ref, ComputedRef, CSSProperties } from 'vue'

interface DropdownPositionPayload {
  elementRef: Ref<HTMLElement | null>
  isAvailablePosition: boolean
}

interface DropdownPositionReturn {
  dropdownTop: ComputedRef<string>
  dropdownBottom: ComputedRef<string>
  dropdownRight: ComputedRef<string>
  dropdownWidth: ComputedRef<string>
  setUpdateDropdownHeight: () => Promise<void>
  dropdownStyle: ComputedRef<CSSProperties | null>
}

export const useDropdownPosition = (payload: DropdownPositionPayload): DropdownPositionReturn => {
  const dropdownMenuHeight = ref(0)
  const rect = useElementBounding(payload.elementRef)
  const { width, height } = useWindowSize()

  const isDropdownFitsBelow = computed(() => {
    const btnBottom = (rect.top.value || 0) + (rect.height.value || 0)
    return btnBottom + dropdownMenuHeight.value <= height.value
  })

  const dropdownTop = computed(() => {
    if (isDropdownFitsBelow.value) {
      const btnBottom = (rect.top.value || 0) + (rect.height.value || 0)
      return `${btnBottom}px`
    }
    return 'auto'
  })

  const dropdownBottom = computed(() => {
    if (!isDropdownFitsBelow.value) {
      const top = rect.top.value || 0
      return `${height.value - top}px`
    }
    return 'auto'
  })

  const dropdownRight = computed(() => {
    const right = width.value - ((rect.left.value || 0) + (rect.width.value || 0))
    return `${Math.max(0, right)}px`
  })

  const dropdownWidth = computed(() => {
    return `${rect.width.value || 0}px`
  })

  const setUpdateDropdownHeight = async (): Promise<void> => {
    await nextTick()
    const dropdownMenu = payload.elementRef.value?.querySelector?.('.vs__dropdown-menu, .multiselect__body')
    // @ts-expect-error offsetHeight
    dropdownMenuHeight.value = (dropdownMenu?.offsetHeight || 200)
  }

  const dropdownStyle = computed<CSSProperties | null>(() => {
    if (!payload.isAvailablePosition) return null

    return {
      '--select-dropdown-top': dropdownTop.value,
      '--select-dropdown-bottom': dropdownBottom.value,
      '--select-dropdown-right': dropdownRight.value,
      '--select-dropdown-width': dropdownWidth.value,
    }
  })

  return {
    dropdownTop,
    dropdownBottom,
    dropdownRight,
    dropdownWidth,
    dropdownStyle,
    setUpdateDropdownHeight,
  }
}
